import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { Link, useNavigate } from 'react-router'
import { notificationsApi, type AppNotification } from '../../lib/api.ts'
import { relativeTime } from '../../lib/relativeTime.ts'
import type { Column, Task, User } from '../../lib/types.ts'
import { useColumns, useTasks, useUsers } from '../../hooks/useTasks.ts'
import { useSession } from '../../hooks/useSession.ts'
import { DueDate, EmptyState, PriorityMeter, Skeleton, StatusPill } from '../../ui/primitives.tsx'
import { Avatar, userColorVar } from '../shell/Avatar.tsx'
import styles from './home.module.css'

/**
 * The landing screen: what needs doing, by me, today.
 *
 * Every section answers a question somebody actually asks when they open the app in the morning —
 * "am I late", "what is mine", "did anyone need me". Deliberately **not** a metrics dashboard:
 * burndown charts and velocity are for reporting upwards, and there is nobody above a team of two.
 *
 * No new endpoints. Everything here is a slice of lists the app already loads, so opening this
 * screen costs nothing the board would not have cost anyway.
 */

const NO_TASKS: Task[] = []

/** Days from today; negative is overdue. Dates are plain YYYY-MM-DD, so no timezone maths. */
function daysUntil(isoDate: string): number {
  const [year = 0, month = 1, day = 1] = isoDate.split('-').map(Number)
  const due = new Date(year, month - 1, day)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return Math.round((due.getTime() - today.getTime()) / 86_400_000)
}

function byUrgency(a: Task, b: Task): number {
  // A deadline outranks a priority label: "late" is a fact, "high" is an opinion.
  const da = a.dueDate ? daysUntil(a.dueDate) : Number.POSITIVE_INFINITY
  const db = b.dueDate ? daysUntil(b.dueDate) : Number.POSITIVE_INFINITY
  if (da !== db) return da - db
  return a.priority - b.priority
}

export function HomeView(): React.JSX.Element {
  const session = useSession()
  const open = useTasks(useMemo(() => ({ scope: 'open' as const }), []))
  const closed = useTasks(useMemo(() => ({ scope: 'closed' as const }), []))
  const columns = useColumns()
  const users = useUsers()

  const inbox = useQuery({
    queryKey: ['notifications'],
    queryFn: () => notificationsApi.list(),
  })

  const me = session.data
  const openTasks = open.data ?? NO_TASKS
  const closedTasks = closed.data ?? NO_TASKS

  const mine = useMemo(
    () => openTasks.filter((t) => t.assigneeId === me?.id).sort(byUrgency),
    [openTasks, me?.id],
  )

  // Late or due within two days. Everyone's, not just mine — at two people, "not my task" is not
  // a reason to let a deadline pass unseen.
  const attention = useMemo(
    () =>
      openTasks
        .filter((t) => t.dueDate !== null && daysUntil(t.dueDate) <= 2)
        .sort(byUrgency)
        .slice(0, 8),
    [openTasks],
  )

  const overdue = useMemo(
    () => openTasks.filter((t) => t.dueDate !== null && daysUntil(t.dueDate) < 0).length,
    [openTasks],
  )

  const closedThisWeek = useMemo(() => {
    const weekAgo = Date.now() - 7 * 86_400_000
    return closedTasks.filter((t) => new Date(t.updatedAt).getTime() >= weekAgo).length
  }, [closedTasks])

  const unread = (inbox.data?.notifications ?? []).filter((n) => n.readAt === null).slice(0, 5)

  const recentlyClosed = useMemo(
    () =>
      [...closedTasks]
        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
        .slice(0, 6),
    [closedTasks],
  )

  const loading = open.isPending || columns.isPending

  return (
    <div className={styles.wrap}>
      <header className={styles.head}>
        <h1 className={styles.title}>
          {greeting()}
          {me ? `, ${me.displayName}` : ''}
        </h1>
        <p className={styles.date}>{today()}</p>
      </header>

      <div className={styles.stats}>
        <Stat label="Otwarte" value={openTasks.length} to="/table" />
        <Stat label="Przypisane do mnie" value={mine.length} to="/table" />
        <Stat
          label="Po terminie"
          value={overdue}
          tone={overdue > 0 ? 'alert' : undefined}
          to="/table"
        />
        <Stat label="Zamknięte w 7 dni" value={closedThisWeek} tone="ok" to="/closed" />
      </div>

      <div className={styles.grid}>
        <Section title="Wymaga uwagi" hint="termin minął albo mija w ciągu dwóch dni">
          {loading ? (
            <Skeletons />
          ) : attention.length === 0 ? (
            <EmptyState text="Nic nie pali się w terminach" variant="plain" />
          ) : (
            <TaskList tasks={attention} columns={columns.data ?? []} users={users.data ?? []} />
          )}
        </Section>

        <Section title="Moje zadania" hint={`${mine.length} otwartych`}>
          {loading ? (
            <Skeletons />
          ) : mine.length === 0 ? (
            <EmptyState text="Nic nie jest na ciebie przypisane" variant="plain" />
          ) : (
            <TaskList
              tasks={mine.slice(0, 8)}
              columns={columns.data ?? []}
              users={users.data ?? []}
              hideAssignee
            />
          )}
        </Section>

        <Section title="Wzmianki" hint="nieprzeczytane">
          {unread.length === 0 ? (
            <EmptyState text="Nikt cię nigdzie nie oznaczył" variant="plain" />
          ) : (
            <ul className={styles.mentions}>
              {unread.map((n) => (
                <MentionRow key={n.id} notification={n} users={users.data ?? []} />
              ))}
            </ul>
          )}
        </Section>

        <Section title="Ostatnio zamknięte" hint="co poszło do przodu">
          {recentlyClosed.length === 0 ? (
            <EmptyState text="Jeszcze nic tu nie trafiło" variant="plain" />
          ) : (
            <TaskList
              tasks={recentlyClosed}
              columns={columns.data ?? []}
              users={users.data ?? []}
              muted
            />
          )}
        </Section>
      </div>
    </div>
  )
}

function greeting(): string {
  const hour = new Date().getHours()
  if (hour < 5) return 'Jeszcze nie śpisz'
  if (hour < 12) return 'Dzień dobry'
  if (hour < 18) return 'Cześć'
  return 'Dobry wieczór'
}

function today(): string {
  return new Date().toLocaleDateString('pl-PL', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  })
}

function Stat({
  label,
  value,
  tone,
  to,
}: {
  label: string
  value: number
  tone?: 'alert' | 'ok' | undefined
  to: string
}): React.JSX.Element {
  const toneClass = tone === 'alert' ? styles.statAlert : tone === 'ok' ? styles.statOk : ''
  return (
    <Link to={to} className={styles.stat}>
      <span className={`${styles.statValue} ${toneClass}`}>{value}</span>
      <span className={styles.statLabel}>{label}</span>
    </Link>
  )
}

function Section({
  title,
  hint,
  children,
}: {
  title: string
  hint?: string
  children: React.ReactNode
}): React.JSX.Element {
  return (
    <section className={styles.panel}>
      <div className={styles.panelHead}>
        <h2 className="section-label">{title}</h2>
        {hint && <span className={styles.hint}>{hint}</span>}
      </div>
      {children}
    </section>
  )
}

function Skeletons(): React.JSX.Element {
  return (
    <div className={styles.skeletons}>
      <Skeleton height={30} delay={0} />
      <Skeleton height={30} delay={200} />
      <Skeleton height={30} delay={400} />
    </div>
  )
}

function TaskList({
  tasks,
  columns,
  users,
  hideAssignee = false,
  muted = false,
}: {
  tasks: Task[]
  columns: Column[]
  users: User[]
  hideAssignee?: boolean
  muted?: boolean
}): React.JSX.Element {
  return (
    <ul className={styles.tasks}>
      {tasks.map((task) => {
        const column = columns.find((c) => c.id === task.columnId)
        const assignee = users.find((u) => u.id === task.assigneeId)
        return (
          <li key={task.id}>
            {/* Every task in the app opens the same drawer, and ?task= is what addresses it. */}
            <Link to={`/table?task=${task.id}`} className={styles.task}>
              <PriorityMeter priority={task.priority} />
              <span className={styles.taskRef}>{task.ref}</span>
              <span className={`${styles.taskTitle}${muted ? ` ${styles.taskMuted}` : ''}`}>
                {task.title}
              </span>
              {task.archivedAt ? (
                <span className={styles.archivedChip}>archiwum</span>
              ) : (
                column && <StatusPill column={column} />
              )}
              {!hideAssignee && assignee && (
                <Avatar
                  name={assignee.displayName}
                  colorVar={userColorVar(assignee.id)}
                  size={18}
                />
              )}
              <DueDate date={task.dueDate} />
            </Link>
          </li>
        )
      })}
    </ul>
  )
}

function MentionRow({
  notification,
  users,
}: {
  notification: AppNotification
  users: User[]
}): React.JSX.Element {
  const navigate = useNavigate()
  const actor = users.find((u) => u.id === notification.actorId)
  const target =
    notification.taskId !== null
      ? `/table?task=${notification.taskId}`
      : `/notes?note=${notification.noteId}`

  return (
    <li>
      <button type="button" className={styles.mention} onClick={() => void navigate(target)}>
        <span className={styles.mentionHead}>
          {actor && <Avatar name={actor.displayName} colorVar={userColorVar(actor.id)} size={18} />}
          {/* Present tense: Polish past tense carries gender and a name does not tell us which. */}
          <span className={styles.mentionWho}>{actor?.displayName ?? 'Ktoś'} wspomina cię w</span>
          <span className={styles.mentionRef}>{notification.taskRef ?? 'notatce'}</span>
          <span className={styles.mentionTime}>{relativeTime(notification.createdAt)}</span>
        </span>
        <span className={styles.mentionExcerpt}>{notification.excerpt}</span>
      </button>
    </li>
  )
}
